import { useEffect, useState } from "react";
import {
  listMembers, saveMember, deleteMember,
  newMemberId, newInviteCode, inviteLink,
  TEAM_ROLES, type TeamMember, type TeamRole,
} from "../lib/team";
import { isCloud } from "../lib/supabase";

const ROLE_BLURB: Record<TeamRole, string> = {
  Contractor: "Submits rehab scope & pricing — roof, HVAC, kitchens. Every number is buffered and flows into your underwriting.",
  Inspector: "Submits condition findings & flags — feeds the compliance checklist and your rehab scope.",
  Estimator: "Submits line-item cost estimates for any scope you assign.",
};

export function Team() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("Contractor");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => { listMembers().then(setMembers).catch(() => {}); }, []);

  async function invite() {
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return;
    setBusy(true);
    const m: TeamMember = { id: newMemberId(), email: e, role, inviteCode: newInviteCode(), status: "pending" };
    await saveMember(m);
    setMembers(await listMembers());
    setEmail("");
    setBusy(false);
  }

  async function remove(id: string) {
    await deleteMember(id);
    setMembers(await listMembers());
  }

  function copy(code: string) {
    const link = inviteLink(code);
    navigator.clipboard?.writeText(link).then(() => { setCopied(code); setTimeout(() => setCopied(null), 1800); }).catch(() => {});
  }

  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ In plain English</div>
        <p>
          Build your <b>ecosystem</b>. Invite your general contractor, inspector, and estimators — each gets a
          role-limited seat that only sees the deals you assign to them, and submits their numbers <b>in your format</b>.
          Their estimates carry your automatic contingency buffer and flow straight into the deal's underwriting.
          You stay in control; they do their part.
        </p>
      </div>

      {/* INVITE */}
      <div className="card pad" style={{ marginBottom: 14 }}>
        <div className="eyebrow">Invite a teammate</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
          <input type="email" placeholder="teammate@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
            className="bb-name" style={{ minWidth: 220 }} />
          <select value={role} onChange={(e) => setRole(e.target.value as TeamRole)} className="bb-name" style={{ minWidth: 150 }}>
            {TEAM_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button className="btn btn-p btn-sm" onClick={invite} disabled={busy || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())}>
            {busy ? "Creating…" : "＋ Create invite"}
          </button>
        </div>
        <div className="note" style={{ marginTop: 8 }}>{ROLE_BLURB[role]}</div>
      </div>

      {/* MEMBERS */}
      <div className="card pad">
        <div className="eyebrow">Your team — {members.length}</div>
        {members.length === 0 ? (
          <div className="note" style={{ marginTop: 10 }}>No teammates yet. Create an invite above, then send the link to your contractor or inspector.</div>
        ) : (
          <div className="tablewrap" style={{ marginTop: 10 }}>
            <table className="t">
              <thead><tr><th>Teammate</th><th>Role</th><th>Status</th><th>Invite link</th><th></th></tr></thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 600, color: "var(--forest)" }}>{m.name || m.email}{m.name && <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 400 }}>{m.email}</div>}</td>
                    <td><span className="tag t-watch">{m.role}</span></td>
                    <td><span className={"tag " + (m.status === "active" ? "t-pass" : "t-watch")}>{m.status === "active" ? "Active" : "Pending"}</span></td>
                    <td>
                      <button className="btn btn-g btn-sm" onClick={() => copy(m.inviteCode)}>{copied === m.inviteCode ? "✓ Copied" : "🔗 Copy link"}</button>
                    </td>
                    <td><button className="rp-x" title="Remove" onClick={() => remove(m.id)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="note" style={{ marginTop: 10 }}>
          Send the invite link however you like. The teammate opens it, sets a password, and lands in their own
          seat — only the deals you assign (in a deal's <b>Rehab &amp; Team</b> section). {isCloud()
            ? "In cloud mode this is a real shared workspace; DB-level access scoping (RLS) is a deploy-hardening step."
            : "Automated email invites arrive once Resend is connected; the link works today."}
        </div>
      </div>
    </div>
  );
}
